import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { mdiClose, mdiProgressHelper } from '@mdi/js';
import { TranslateService } from '@ngx-translate/core';
import jss, { Rule, StyleSheet } from 'jss';
import * as React from 'react';
import { connect } from 'react-redux';
import { interval, timer } from 'rxjs';
import { debounceTime, filter, takeWhile, tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import { CmpsDecimalPipe, CmpsPercentPipe } from '../common/number.pipe';
import { createTab } from '../human-insight/human-insight.model';
import {
    ConfigPath,
    ConfigPathLevel,
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    createLevelPath,
    findLevel,
    HierarchyUserInfoItem,
    Metric,
    PillarsInGrid,
    RoleHierarchyItem,
} from '../insight/config.insight.model';
import {
    autoSizeColumnTree,
    Column,
    Controller,
    DivRenderer,
    DivRendererOptions,
    flattenArrayWithObj,
    GridNode,
    inverseSortOrder,
    RowNode,
    Sort,
    SortOrder,
} from '../insight/experimental.grid';
import {
    ColumnContext,
    ColumnID,
    ColumnType,
    concatLevelDataMap,
    DataSource,
    GridInsightNewTabClickEvent,
    GridInsightSelectionChangeEvent,
    GridInsightSelectionItem,
    GridInsightState,
    Refs,
    RowContext,
    RowId,
} from '../insight/grid.insight.model';
import {
    ColumnElement,
    MetricElement,
    NodeElement,
    render,
} from '../insight/grid.insight.render';
import {
    gridRetrieveData,
    gridRetrieveDataCancel,
    gridSort,
    gridToggleColumn,
} from '../insight/insight.action';
import { InsightEpicBridge } from '../insight/insight.epic.bridge';
import { OptionalColumn } from '../insight/optional-column.insight.model';
import {
    getEventDateValue,
    PeriodTypeOpts,
} from '../insight/period.insight.model';
import { CacheDateItem } from '../performance-data/cache-date.pd.model';
import {
    DataFilterOptions,
    LevelDataMap,
} from '../performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../performance-data/metric.pd.model';
import iocTypes from '../rvirus.imap';
import { lazyInject } from '../rvirus.ioc';
import { User } from '../user/user.model';
import { PeriodType } from '../widget/date-range.widget.model';

import { GridPerformanceInsightProps } from './grid.pi.model';
import { PiTabConfig } from './performance-insight.model';

// TODO de-dup and merge into epics
class GridPerformanceInsightImpl extends React.Component<
    GridPerformanceInsightProps,
    GridInsightState
> {
    @lazyInject(iocTypes.TranslateService.identifier)
    private readonly _translateService: TranslateService;

    @lazyInject(iocTypes.CmpsDecimalPipe.identifier)
    private readonly _decimalPipe: CmpsDecimalPipe;

    @lazyInject(iocTypes.CmpsPercentPipe.identifier)
    private readonly _percentPipe: CmpsPercentPipe;

    @lazyInject(iocTypes.CmpsDatePipe.identifier)
    private readonly _datePipe: CmpsDatePipe;

    @lazyInject(iocTypes.InsightEpicBridge.identifier)
    private readonly _epicBridge: InsightEpicBridge;

    private readonly _refs: Refs;
    private readonly _container: React.RefObject<HTMLDivElement>;
    private _renderer: DivRenderer;
    private _grid: GridNode;
    private readonly _dataSource: DataSource;
    private readonly _sheet: StyleSheet;
    private readonly _contentRule: Rule;
    private _rootRowId: string;

    constructor(props: GridPerformanceInsightProps) {
        super(props);

        this.state = {};

        this._container = React.createRef();

        this._dataSource = new DataSource();
        this._dataSource.columnTpl = ColumnElement;
        this._dataSource.nodeTpl = NodeElement;
        this._dataSource.metricTpl = MetricElement;

        this._refs = {
            translate: this._translateService,
            cmpsNumber: this._decimalPipe,
            cmpsPercent: this._percentPipe,
            onExpandPillarClick: this.onExpandPillarClick.bind(this),
            onExpandMetricClick: this.onExpandMetricClick.bind(this),
            onExpandRowClick: this.onExpandRowClick.bind(this),
            onSortClick: this.onSortClick.bind(this),
            onNewTabClick: this.onNewTabClick.bind(this),
            onToggleSelectionItemClick:
                this.onToggleSelectionItemClick.bind(this),
        };

        this._sheet = jss.createStyleSheet({}, { link: true });
        this._contentRule = this._sheet.addRule('content', {});
    }

    componentDidMount(): void {
        this._inflate();
        this._sheet.attach();
    }

    componentDidUpdate(
        prevProps: Readonly<GridPerformanceInsightProps>,
        prevState: Readonly<{}>,
        snapshot?: any,
    ): void {
        try {
            this._renderer.opts.height = this.props.maxHeight;
            this._container.current.children.length === 0 &&
                this._container.current.appendChild(
                    this._grid.renderContext.element,
                );
        } catch {}

        if (prevProps.path !== this.props.path) {
            this._dataSource.path = this.props.path;
            this._grid.removeChildren();
            this._getData();
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const layoutAndAutoSize = (
            columns: Array<Column<ColumnContext>>,
            force?: boolean,
        ) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._renderer
                .layout()
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const as =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        columns &&
                        columns.filter(
                            (e) => (force || !e.ctx.autoSized) && !e.hidden,
                        );
                    return (
                        as &&
                        autoSizeColumnTree(
                            this._renderer.contentColumns.columns.children,
                            this._renderer,
                            as,
                        ).then(() => {
                            // eslint-disable-next-line no-return-assign
                            as.forEach((e) => (e.ctx.autoSized = true));
                        })
                    );
                });
        };

        if (
            prevProps.pillarSelection !== this.props.pillarSelection ||
            prevProps.columnSelection !== this.props.columnSelection
        ) {
            const [unchanged, changed] = this._dataSource.applyHiddenColumns(
                this.props.pillarSelection,
                this.props.columnSelection,
                this._renderer,
            );
            if (prevProps.columnSelection !== this.props.columnSelection) {
                layoutAndAutoSize(unchanged, true);
            }
            layoutAndAutoSize(changed, true);
        }

        if (prevProps.trendVisible !== this.props.trendVisible) {
            this._grid.context.ctx.trendVisible = this.props.trendVisible;
            this._grid.forEach((e) =>
                e.cells.at(0).detectChanges({ emitEvent: false }),
            );
            // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async
            timer().subscribe(() =>
                this._renderer.autoSizeColumns([ColumnID.NODE.toString()]),
            );
        }

        if (
            prevProps.trendHideFirstGridColumn !==
            this.props.trendHideFirstGridColumn
        ) {
            this._layout();
        }

        if (prevProps.sort !== this.props.sort) {
            this._grid.context.ctx.sort = { ...this.props.sort };
            const sortDefs: Sort[] = [{ ...this.props.sort.def }];
            if (this.props.sort.def.columnId !== ColumnID.NODE.toString()) {
                sortDefs.push({
                    columnId: ColumnID.NODE.toString(),
                    order: SortOrder.ASC,
                });
            }
            if (
                !this.props.sort ||
                !this.props.sort.rowIds ||
                this.props.sort.rowIds.length === 0
            ) {
                this._grid.sort(sortDefs);
            } else {
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                this._grid
                    .filter(
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        (n) => this.props.sort.rowIds.indexOf(n.def.id) !== -1,
                    )
                    .forEach((n) =>
                        n.sort(sortDefs, null, { emitEvent: true }),
                    );
            }
            this._renderer.flatten();
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._renderer.layout();
        }

        // from store
        if (prevProps.pending.data !== this.props.pending.data) {
            this._dataSource.data = this.props.data;
            this._dataSource.peerGroupData = this.props.peerGroupData;
            this._dataSource.hierarchyUserInfo = this.props.hierarchyUserInfo;
            this._dataSource.hierarchyUserInfoCurrent =
                this.props.hierarchyUserInfoCurrent;
            const rowIds = Object.keys(this.props.pending.data).filter(
                (k) => prevProps.pending.data[k] && !this.props.pending.data[k],
            );
            rowIds.forEach((e) => {
                const isRoot = e === this._rootRowId;
                const row = !isRoot && this._grid.find((n) => n.def.id === e);
                if (!isRoot && !row) return;

                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (row && row.context.ctx.cancelled) {
                    row.context.ctx.pending = false;
                    row.context.ctx.cancelled = false;
                    return;
                }

                const levels = isRoot
                    ? this.props.path.levels
                    : findLevel(
                          this.props.path.levels,
                          row.context.ctx.levelDataMap.identifier,
                      ).levels;
                let rows = this._dataSource.makeRows(
                    isRoot ? this._rootRowId : row.def.id,
                    levels,
                );

                if (row) {
                    row.context.ctx.pending = false;
                    row.context.ctx.expanded = rows.length !== 0; // TODO revise. can't tell if expand was cancelled
                    row.def.children = rows;
                    rows = [row.def];
                }

                this._grid.updateChildren(rows);

                (!row ? this._grid.children : row.children)
                    .filter((ee) => ee.context.ctx.autoExpand)
                    .forEach((ee) => this.onExpandRowClick(ee));

                if (isRoot && this._grid.length !== 0) {
                    this.onToggleSelectionItemClick(this._grid.at(0), true);
                }

                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                row && row.cells.at(0).detectChanges({ emitEvent: false });
            });
        }

        // initial expanded column is handled in _inflate()
        if (
            prevProps.lastToggleColumnState !== this.props.lastToggleColumnState
        ) {
            if (this.props.lastToggleColumnState) {
                if (
                    this._grid.context.ctx.pendingColumnMovementReset &&
                    this.props.lastToggleColumnState.pillarExpanded
                ) {
                    this._grid.context.ctx.pendingColumnMovementReset = false;
                    this._renderer.resetMovedColumns();
                    this._dataSource.columnsFlatten = flattenArrayWithObj(
                        this._dataSource.columns,
                        'children',
                    );
                    this._dataSource.reassignPillarHandles();
                }
                this._dataSource.applyExpandedColumnState(
                    this.props.lastToggleColumnState,
                );
            }
            const [, changed] = this._dataSource.applyHiddenColumns(
                this.props.pillarSelection,
                this.props.columnSelection,
                this._renderer,
            );
            layoutAndAutoSize(changed);
        }

        if (prevProps.export !== this.props.export) {
            //
        }
    }

    componentWillUnmount(): void {
        this._sheet.detach();
        this._deflate();
    }

    onExpandPillarClick(column: Column<ColumnContext>): void {
        this.props.dispatch(
            gridToggleColumn(column.id, !column.ctx.pillarExpanded, undefined),
        );
    }

    onExpandMetricClick(column: Column<ColumnContext>): void {
        this.props.dispatch(
            gridToggleColumn(column.id, undefined, !column.ctx.metricExpanded),
        );
    }

    onExpandRowClick(row: RowNode<RowContext>): void {
        if (row.context.ctx.pending) {
            row.context.ctx.pending = false;
            row.context.ctx.cancelled = true;
            this.props.dispatch(gridRetrieveDataCancel([row.def.id]));
        } else if (!row.context.ctx.expanded) {
            row.context.ctx.pending = true;
            this._getData(row);
        } else {
            row.context.ctx.pending = false;
            row.context.ctx.expanded = false;
            row.removeChildren();
            this._updateSelection();
        }
        row.cells.at(0).detectChanges({ emitEvent: false });
    }

    onSortClick(column: Column<ColumnContext>): void {
        const maxDepth =
            this._grid.reduce<number>(
                (p, c) => Math.max(c.context.ctx.depth, p),
                0,
            ) - 1;
        const rowIds =
            maxDepth >= 0 &&
            this._grid
                .filter((n) => n.context.ctx.depth === maxDepth)
                .map((n) => n.def.id);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.props.dispatch(
            gridSort(
                rowIds,
                inverseSortOrder(
                    column,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.props.sort && this.props.sort.def,
                    SortOrder.ASC,
                ),
            ),
        );
    }

    onNewTabClick(row: RowNode<RowContext>): void {
        if (!row.context.ctx.allowNewTab) return;
        const user = new User();
        user.id = row.context.ctx.levelDataMap.dataId;
        user.lastName = row.cells.at(0).context.ctx.value.name;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.props.onNewTabClick &&
            this.props.onNewTabClick({ tab: createTab(user) });
    }

    onToggleSelectionItemClick(
        row: RowNode<RowContext>,
        rootLoad?: boolean,
    ): void {
        const nodeEl = row.cells.at(0);
        const numSelected = this._grid.filter(
            (r) => r.context.ctx.selected,
        ).length;
        if (!nodeEl.context.ctx.value.unSelectable) {
            this.setState({
                ...this.state,
                dismissedMaxSelection: numSelected === this.props.maxSelection,
            });
            if (
                !row.context.ctx.selected &&
                this.props.maxSelection != null &&
                numSelected === this.props.maxSelection
            ) {
                this.setState({ ...this.state, dismissedMaxSelection: false });
                return;
            }
            row.context.ctx.selected = !row.context.ctx.selected;
            nodeEl.detectChanges({ emitEvent: false });
        }
        (rootLoad || !nodeEl.context.ctx.value.unSelectable) &&
            this._updateSelection();
    }

    private _getData(row?: RowNode): void {
        const root = !row;
        const identifier = root
            ? null
            : row.context.ctx.levelDataMap.identifier;
        const rowId = root
            ? RowId.create(
                  'ROOT',
                  createLevelPath(this.props.path.levels, 'ROOT'),
                  -1,
                  0,
                  -1,
                  -1,
                  'ROOT',
              )
            : row.def.id;
        const levelsDataMap = this._dataSource.processInputCriteriaIdTypes(row);

        if (root) {
            this._rootRowId = rowId;
            this._grid.context.ctx.rowId = this._rootRowId;
            this._grid.context.ctx.levelsDataMap = levelsDataMap;
            const addLevelDataMap = (
                identifier: string,
                viewType: PerformanceDataViewType,
                outputCriteriaType: CriteriaIdType,
                dataId: number,
            ): void => {
                levelsDataMap.push({
                    identifier,
                    performanceDataViewType: viewType,
                    displayNameCriteriaIdType: null,
                    outputCriteriaType,
                    dataId,
                    outputCriteriaTypes: [outputCriteriaType],
                    outputCriteriaTypeArgs: { [outputCriteriaType]: dataId },
                });
            };
            this.props.path.levels.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.lobIdList &&
                    this.props.initialDataFilterOpts.lobIdList.forEach((ee) =>
                        addLevelDataMap(
                            e.identifier,
                            PerformanceDataViewType.LOB,
                            CriteriaIdType.LOB_ID,
                            ee,
                        ),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.regionIdList &&
                    this.props.initialDataFilterOpts.regionIdList.forEach(
                        (ee) =>
                            addLevelDataMap(
                                e.identifier,
                                PerformanceDataViewType.REGION,
                                CriteriaIdType.REGION_ID,
                                ee,
                            ),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.areaIdList &&
                    this.props.initialDataFilterOpts.areaIdList.forEach((ee) =>
                        addLevelDataMap(
                            e.identifier,
                            PerformanceDataViewType.AREA,
                            CriteriaIdType.AREA_ID,
                            ee,
                        ),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.siteIdList &&
                    this.props.initialDataFilterOpts.siteIdList.forEach((ee) =>
                        addLevelDataMap(
                            e.identifier,
                            PerformanceDataViewType.SITE,
                            CriteriaIdType.SITE_ID,
                            ee,
                        ),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.peerGroupIdList &&
                    this.props.initialDataFilterOpts.peerGroupIdList.forEach(
                        (ee) =>
                            addLevelDataMap(
                                e.identifier,
                                PerformanceDataViewType.PEER_GROUP,
                                CriteriaIdType.PEER_GROUP_ID,
                                ee,
                            ),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.props.initialDataFilterOpts.superGroupIdList &&
                    this.props.initialDataFilterOpts.superGroupIdList.forEach(
                        (ee) =>
                            addLevelDataMap(
                                e.identifier,
                                PerformanceDataViewType.SUPER_GROUP,
                                CriteriaIdType.SUPER_GROUP_ID,
                                ee,
                            ),
                    );
            });
        }

        this.props.dispatch(
            gridRetrieveData(
                {
                    identifier,
                    viewType: null, // dataForLevels is called, so this and related can be null
                    displayNameCriteriaIdType: null,
                    outputCriteriaIdType: null,
                    outputCriteriaIdTypes: null,
                    inputCriteriaIdTypes: null,
                    periodType: this.props.periodType,
                    date: this.props.eventDate,
                    metricIdList: this._dataSource.metrics.map(
                        (e) => e.metricId,
                    ),
                },
                rowId,
                levelsDataMap,
            ),
        );
    }

    private _updateSelection(): void {
        const selected = this._grid
            .filter((r) => r.context.ctx.selected)
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            .map(
                (r) =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({
                        rowId: r.def.id,
                        levelsDataMap:
                            this._dataSource.processInputCriteriaIdTypes(
                                r.parent instanceof RowNode ? r.parent : null,
                            ),
                        levelsDataMapRow: concatLevelDataMap(r, 0),
                        name: r.cells.at(0).context.ctx.value.name,
                    }) as GridInsightSelectionItem,
            );
        this.setState({
            ...this.state,
            numSelected: selected.length,
            dismissedMaxSelection: selected.length !== this.props.maxSelection,
        });
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.props.onSelectionChange &&
            this.props.onSelectionChange({ selected });
    }

    private _inflate(): void {
        this._dataSource.path = this.props.path;
        this._dataSource.didProcessInputCriteriaIdTypes = (
            levels: ConfigPathLevel[],
            parentLevelsDataMap: LevelDataMap[],
            levelsDataMap: LevelDataMap[],
        ): void => {
            levels.forEach((e) => {
                parentLevelsDataMap.forEach((ee) => {
                    levelsDataMap.push({
                        identifier: e.identifier,
                        performanceDataViewType: ee.performanceDataViewType,
                        displayNameCriteriaIdType: null,
                        outputCriteriaType: ee.outputCriteriaType,
                        dataId: ee.dataId,
                        outputCriteriaTypes: ee.outputCriteriaTypes
                            ? [...ee.outputCriteriaTypes]
                            : null,
                        outputCriteriaTypeArgs: ee.outputCriteriaTypeArgs
                            ? { ...ee.outputCriteriaTypeArgs }
                            : null,
                    });
                });
            });
        };
        this._dataSource.makeColumns(
            this.props.viewPillar,
            this.props.viewMetric,
            this.props.pillars,
            this.props.columns,
            this.props.metrics,
            this.props.view.pillarHeadersExpandedInGrid,
            this.props.view.pillarsInGrid !== PillarsInGrid.ALL,
        );
        // this._dataSource.applyHiddenColumns(this.props.pillarSelection, this.props.columnSelection);

        const periodTypeOpt = PeriodTypeOpts.find(
            (e) => e.value === this.props.periodType,
        );
        const periodValue = periodTypeOpt
            ? this._translateService.instant(periodTypeOpt.tlKey)
            : 'N/A';
        const dateRangeValue =
            getEventDateValue(
                this.props.cacheDates,
                this.props.eventDate,
                this.props.periodType,
                this._datePipe,
            ) || 'N/A';
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        this._dataSource.columns[0].ctx.name = `${periodValue}\n${dateRangeValue}`;

        try {
            this._renderer = Controller.usingDef<
                DivRenderer,
                DivRendererOptions
            >(
                DivRenderer,
                {
                    ctx: {
                        trendVisible: this.props.trendVisible,
                        showThresholdColours:
                            this.props.view.showThresholdColours,
                        showTrendArrows: this.props.view.showTrendArrows,
                        pendingColumnMovementReset: false,
                        refs: this._refs,
                    },
                },
                {
                    columns: this._dataSource.columns,
                    defaultRenderFn: render,
                    horizontalScrollPadding: 4000,
                },
            );
            this._grid = this._renderer.gridNode;
            this._renderer.layoutt
                .asObservable()
                .pipe(
                    debounceTime(1000),
                    filter(() => !!this._renderer),
                    tap(() => {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this._renderer.autoSizeColumns([
                            ColumnID.NODE.toString(),
                        ]);
                        this._layout();
                    }),
                )
                .subscribe();
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._renderer
                .layout()
                .then(() => {
                    this._dataSource.applyHiddenColumns(
                        this.props.pillarSelection,
                        this.props.columnSelection,
                        this._renderer,
                    );
                    interval(100)
                        .pipe(
                            takeWhile(() => {
                                try {
                                    return (
                                        this._container.current.children
                                            .length === 0
                                    );
                                } catch {
                                    return true;
                                }
                            }),
                        )
                        .subscribe({
                            complete: () => {
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                this._renderer.autoSizeColumnTree().then(() => {
                                    // eslint-disable-next-line no-return-assign
                                    this._dataSource.columnsFlatten
                                        .filter((e) => !e.hidden)
                                        .forEach(
                                            (e) => (e.ctx.autoSized = true),
                                        );
                                });
                            },
                        });
                })
                .then(() => {
                    this._renderer.contentColumns.columns
                        .find(
                            (e) =>
                                e.cells.at(0).context.c.ctx.type ===
                                ColumnType.METRIC,
                        )
                        .drag.asObservable()
                        .pipe(
                            filter(
                                (e) => e.which === 'drop' && e.drag !== e.drop,
                            ),
                            tap((e) => {
                                let comps = e.columnsSrc.filter(
                                    (ee: Column<ColumnContext>) =>
                                        ee.ctx.parentId === e.drag.context.c.id,
                                );
                                this._renderer.moveColumns(
                                    comps.map((ee) => ee.id),
                                    e.drag.context.c.id,
                                    'after',
                                );

                                comps = e.columnsSrc.filter(
                                    (ee: Column<ColumnContext>) =>
                                        ee.ctx.parentId === e.drop.context.c.id,
                                );
                                this._renderer.moveColumns(
                                    comps.map((ee) => ee.id),
                                    e.drop.context.c.id,
                                    'after',
                                );

                                this._dataSource.columnsFlatten =
                                    flattenArrayWithObj(
                                        this._dataSource.columns,
                                        'children',
                                    );

                                if (
                                    e.drag.context.ctx.hasPillarHandle ||
                                    e.drop.context.ctx.hasPillarHandle ||
                                    e.columnsDest !== e.columnsSrc
                                ) {
                                    this._dataSource.reassignPillarHandles();
                                }
                                if (e.columnsDest !== e.columnsSrc) {
                                    this._dataSource.collapseAllPillars();
                                    this._grid.context.ctx.pendingColumnMovementReset =
                                        true;
                                }
                                // if (e.drop.context.ctx.hasPillarHandle || !this.props.lastToggleColumnState) {
                                if (e.columnsDest !== e.columnsSrc) {
                                    this._dataSource.applyHiddenColumns(
                                        this.props.pillarSelection,
                                        this.props.columnSelection,
                                        this._renderer,
                                    );
                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                    this._renderer.layout();
                                } else {
                                    // this.props.dispatch(gridResetToggleColumn());
                                }
                            }),
                        )
                        .subscribe();
                });
            this._epicBridge.renderer = this._renderer;
        } catch (e) {
            console.log(e);
        }

        this._getData();
    }

    private _deflate(): void {
        this.props.dispatch(gridRetrieveDataCancel());

        try {
            this._grid.renderContext.element.parentElement.removeChild(
                this._grid.renderContext.element,
            );
        } catch {}
        try {
            this._renderer.destroy(this._grid);
        } catch (e) {
            console.log(e);
        }
        this._grid = null;
        this._renderer = null;
        this._epicBridge.renderer = null;
    }

    private _layout(): void {
        const leftVp = this._renderer.columns.left.viewport;
        this._contentRule.prop(
            'left',
            `${
                !this.props.trendHideFirstGridColumn ? leftVp.clientWidth : 0
            }px`,
        );

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.props.onLayout &&
            this.props.onLayout(this._contentRule.selector.substr(1));
    }

    render(): React.ReactNode {
        return (
            <>
                {this.props.maxSelection != null &&
                    this.state.numSelected === this.props.maxSelection &&
                    !this.state.dismissedMaxSelection && (
                        <div
                            className="alert alert-warning justify-content-between insight-grid-alert cmps-cdk-d-flex cmps-cdk-align-items-center"
                            role="alert"
                        >
                            Maximum number of items ({this.props.maxSelection})
                            plotted.
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() =>
                                    this.setState({
                                        ...this.state,
                                        dismissedMaxSelection: true,
                                    })
                                }
                            >
                                <div className="d-inline cmps-icon">
                                    <svg>
                                        <path d={mdiClose}></path>
                                    </svg>
                                </div>
                            </button>
                        </div>
                    )}
                {this._rootRowId &&
                    this.props.pending.data[this._rootRowId] && (
                        <div className="d-flex justify-content-center">
                            <div className="d-inline cmps-icon">
                                <svg>
                                    <path d={mdiProgressHelper}></path>
                                </svg>
                            </div>
                        </div>
                    )}
                {this._rootRowId &&
                    !this.props.pending.data[this._rootRowId] && (
                        <div
                            className={`insight-grid ${
                                !this.props.trendVisible
                                    ? ''
                                    : 'insight-trend-visible'
                            } ${this.props.className}`}
                            ref={this._container}
                        />
                    )}
            </>
        );
    }
}

export const GridPerformanceInsight = connect<
    {},
    {},
    GridPerformanceInsightProps,
    any
>((state, ownProps) => ({ ...ownProps, ...state.insight.grid }))(
    GridPerformanceInsightImpl,
);

@Component({
    selector: '[rvirus][cmpsPerformanceInsightGrid]',
    template: ` <cmps-performance-insight-grid
        [config]="config"
        [view]="view"
        [metrics]="metrics"
        [periodType]="periodType"
        [cacheDates]="cacheDates"
        [eventDate]="eventDate"
        [initialDataFilterOpts]="initialDataFilterOpts"
        [viewPillar]="viewPillar"
        [viewMetric]="viewMetric"
        [path]="path"
        [roleHierarchy]="roleHierarchy"
        [pillars]="pillars"
        [pillarSelection]="pillarSelection"
        [columns]="columns"
        [columnSelection]="columnSelection"
        [maxSelection]="maxSelection"
        [configUserInfo]="configUserInfo"
        [className]="className"
        [maxHeight]="maxHeight"
        [trendVisible]="trendVisible"
        [trendHideFirstGridColumn]="trendHideFirstGridColumn"
        (onLayout)="layout.emit($event)"
        (onNewTabClick)="newTabClick.emit($event)"
        (onSelectionChange)="selectionChange.emit($event)"
    ></cmps-performance-insight-grid>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class GridPerformanceInsightComponent {
    @Input() config: PiTabConfig;
    @Input() view: ConfigView;
    @Input() metrics: Metric[];
    @Input() periodType: PeriodType;
    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Input() initialDataFilterOpts: DataFilterOptions;
    @Input() viewPillar: ConfigViewPillar;
    @Input() viewMetric: ConfigViewMetric;
    @Input() path: ConfigPath;
    @Input() roleHierarchy: RoleHierarchyItem[];
    @Input() pillars: ConfigViewPillar[];
    @Input() pillarSelection: ConfigViewPillar[];
    @Input() columns: OptionalColumn[];
    @Input() columnSelection: OptionalColumn[];
    @Input() maxSelection: number;
    @Input() configUserInfo: HierarchyUserInfoItem;
    @Input() className: string;
    @Input() maxHeight: number;
    @Input() trendVisible: boolean;
    @Input() trendHideFirstGridColumn: boolean;
    @Output() layout: EventEmitter<string> = new EventEmitter();
    @Output() newTabClick: EventEmitter<GridInsightNewTabClickEvent> =
        new EventEmitter();

    @Output() selectionChange: EventEmitter<GridInsightSelectionChangeEvent> =
        new EventEmitter();
}
